import { noop } from '@aloft/toolkit-shared';

import { RefObject, useEffect, useState } from 'react';

import { useLatLngFromAddress } from './useLatLngFromAddress.ts';
import { useLocationHasStreetView } from './useLocationHasStreetView.ts';
import { useStreetViewPanorama } from './useStreetViewPanorama.ts';

export const useStreetView = (
  streetViewRef: RefObject<HTMLDivElement>,
  address?: string,
  fallbackPosition?: google.maps.LatLngLiteral,
) => {
  const [status, setStatus] = useState<'idle' | 'pending' | 'loaded' | 'error'>('idle');

  const streetViewPanorama = useStreetViewPanorama(streetViewRef);
  const locationHasStreetView = useLocationHasStreetView();

  const { latLng, status: geocodedStatus } = useLatLngFromAddress(address, true);

  useEffect(() => {
    const setStreetViewPosition = async (position: google.maps.LatLngLiteral) => {
      if (!locationHasStreetView || !streetViewPanorama) {
        setStatus('pending');
        return;
      }

      const hasStreetView = await locationHasStreetView(position);

      if (!hasStreetView) {
        streetViewPanorama.setOptions({ visible: false });
        setStatus('error');
        return;
      }

      streetViewPanorama.setOptions({ position, visible: true });
      setStatus('loaded');
    };

    if (geocodedStatus === 'pending') return;
    const geocodedPosition = latLng ? { lat: latLng.latitude, lng: latLng.longitude } : undefined;
    const position = geocodedPosition ?? fallbackPosition;

    if (!position) {
      setStatus('error');
      return;
    }

    setStreetViewPosition(position).then(noop).catch(console.error);
  }, [locationHasStreetView, streetViewPanorama, fallbackPosition, geocodedStatus, latLng]);

  return status;
};
